/**
 * Hooks de chargement.
 *
 * Pas de React Query ici (aucune dépendance ajoutée, c'est une contrainte du
 * projet) : un dashboard n'a besoin que de trois choses — un état de chargement,
 * un état d'erreur exploitable, et un rechargement périodique annulable.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  /** Horodatage du dernier succès — sert à afficher « à jour il y a X ». */
  updatedAt: number | null;
  reload: () => void;
}

export interface UseApiOptions {
  /** Période de rafraîchissement automatique, en millisecondes. */
  refreshMs?: number;
  /** Quand `false`, aucune requête n'est émise (page non ouverte, jeton absent). */
  enabled?: boolean;
}

/**
 * @param fetcher reçoit un `AbortSignal` : changer de période annule la requête
 *   en vol, sinon une réponse lente écrase l'affichage de la nouvelle période.
 * @param depsKey chaîne stable décrivant les paramètres. On passe une chaîne
 *   plutôt qu'un tableau pour que la comparaison soit une égalité simple.
 */
export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  depsKey: string,
  options: UseApiOptions = {},
): AsyncState<T> {
  const { refreshMs, enabled = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);

  // Le fetcher est recréé à chaque rendu par l'appelant ; on le garde dans une
  // ref pour que l'effet ne dépende que de `depsKey`.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let alive = true;

    const run = async (): Promise<void> => {
      if (alive) setLoading(true);
      try {
        const result = await fetcherRef.current(controller.signal);
        if (!alive) return;
        setData(result);
        setError(null);
        setUpdatedAt(Date.now());
      } catch (cause) {
        if (!alive) return;
        // Une annulation n'est pas une erreur à montrer : on laisse l'écran
        // dans son état précédent, la requête suivante prend le relais.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, { title: String(cause) }, true));
      } finally {
        if (alive) setLoading(false);
      }
    };

    void run();

    let timer: number | undefined;
    if (refreshMs && refreshMs > 0) {
      timer = window.setInterval(() => {
        void run();
      }, refreshMs);
    }

    return () => {
      alive = false;
      controller.abort();
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [depsKey, nonce, refreshMs, enabled]);

  return { data, error, loading, updatedAt, reload };
}

/**
 * Horloge à la seconde, pour les mentions « il y a X ».
 * Un seul intervalle par composant qui en a besoin, jamais un par tuile.
 */
export function useTick(periodMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), periodMs);
    return () => window.clearInterval(id);
  }, [periodMs]);
  return now;
}

/**
 * Routage par fragment d'URL (`#/usage`).
 *
 * Pas de react-router : le dashboard a six pages fixes, et un routage par hash
 * fonctionne aussi bien derrière un `vite preview` que sur un hébergement
 * statique sans règle de réécriture.
 */
export function useHashRoute(fallback: string): string {
  const read = (): string => {
    const raw = window.location.hash.replace(/^#\/?/, '');
    return raw === '' ? fallback : raw;
  };
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const onChange = (): void => setRoute(read());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
    // `fallback` est une constante d'application : pas de dépendance nécessaire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return route;
}
